import "./style.css";

import React, { useState } from "react";

import monitorApi from "../../../api/monitorApi";
import spinner from "../../../assets/img/spinner.svg";
import { DEFAULT_COLUMNS } from "../config/config";
import { DnsRecordType } from "../MonitoringPageTypes";
import MonitoringTableHead from "../MonitoringTableHead/MonitoringTableHead";
import {
  buildDnsTooltip,
  buildSimpleTooltip,
  HeaderHelpIcon,
  HoverTooltip,
} from "../MonitoringTooltip/MonitoringTooltip";
import { useMonitoringTooltip } from "../useMonitoringTooltip/useMonitoringTooltip";
import { CheckResultType } from "./CheckDomainTypes";

function getNsdiStatus(nsdiValue: string | null | undefined) {
  if (nsdiValue === "resolved")
    return { label: "resolved", variant: "resolved" };
  if (nsdiValue === "no_data") return { label: "no_data", variant: "unknown" };
  if (nsdiValue && nsdiValue !== "resolved" && nsdiValue !== "no_data")
    return { label: nsdiValue, variant: "unresolved" };
  return { label: "unresolved", variant: "unresolved" };
}

function getDnsStatus(dnsRecords: DnsRecordType[]) {
  const list = Array.isArray(dnsRecords) ? dnsRecords : [];
  const resolvedCount = list.filter((item) => item.resolved).length;
  const totalCount = list.length;
  if (totalCount === 0 || resolvedCount === 0) {
    const unresolved = list.filter((x) => x && !x.resolved);
    const allNxdomain =
      unresolved.length > 0 &&
      unresolved.every(
        (x) => x.error && String(x.error).toUpperCase().includes("NXDOMAIN"),
      );
    if (allNxdomain) {
      return { label: "NXDOMAIN", variant: "unresolved" };
    }
    return { label: "unresolved", variant: "unresolved" };
  }
  if (resolvedCount === totalCount) {
    return { label: "resolved", variant: "resolved" };
  }
  return { label: "partial", variant: "partial" };
}

const CheckDomain: React.FC = () => {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CheckResultType | null>(null);

  const {
    tooltip,
    showTooltip,
    moveTooltip,
    hideTooltip,
    tooltipRootRef,
    tooltipScrollRef,
    setScrollHover,
  } = useMonitoringTooltip();

  const getErrorMessage = (err: any) => {
    const status = err.response?.status;
    const isNetworkError =
      !err.response &&
      (err.code === "ERR_NETWORK" ||
        err.code === "ECONNREFUSED" ||
        err.code === "ETIMEDOUT" ||
        err.message?.includes("Network Error"));

    if (status === 404) {
      return {
        type: "404",
        title: "Страница не найдена",
        message: "Запрашиваемый API-эндпоинт не существует (404).",
      };
    }
    if (status >= 500) {
      const msg =
        err.response?.data?.error || err.response?.data?.detail || err.message;
      return {
        type: "server",
        title: "Сервер недоступен",
        message:
          msg && typeof msg === "string"
            ? msg
            : "Внутренняя ошибка сервера. Попробуйте позже.",
      };
    }
    if (isNetworkError) {
      return {
        type: "server",
        title: "Сервер не отвечает",
        message:
          "Не удалось подключиться к серверу. Проверьте подключение к интернету или попробуйте позже.",
      };
    }
    const resp = err.response;
    const respData = resp?.data;
    const msg =
      respData?.error ||
      respData?.detail ||
      err.message ||
      "Ошибка при проверке домена";
    return {
      type: "validation",
      title: "Ошибка",
      message: typeof msg === "string" ? msg : JSON.stringify(msg),
    };
  };

  const handleCheck = async () => {
    if (!value.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const data = await monitorApi.checkDnsByDomain(value.trim());
      setResult(data);
    } catch (err) {
      setResult({ error: getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="check-domain-wrapper">
      <div className="flex items-center gap-[1rem] w-full max-w-[900px] mx-auto">
        <div className="check-domain-input-wrap">
          <input
            className="check-domain-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCheck()}
            placeholder="Проверка DNS по домену"
            disabled={loading}
          />
          {value && (
            <button
              className="check-domain-input-clear"
              onClick={() => setValue("")}
              disabled={loading}
              aria-label="Очистить"
              type="button"
            >
              ×
            </button>
          )}
        </div>
        <button
          className="check-domain-button"
          onClick={handleCheck}
          disabled={loading}
        >
          Проверить
        </button>
      </div>
      {loading && (
        <div className="check-domain-loading mb-1">
          <img
            src={spinner}
            alt="Загрузка..."
            width={36}
            height={36}
            className="animate-spin"
          />
        </div>
      )}
      {!loading && result?.error && (
        <div
          className={`check-domain-error-box check-domain-error-${result.error.type}`}
        >
          <div className="check-domain-error-icon">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className="check-domain-error-content">
            <div className="check-domain-error-title">{result.error.title}</div>
            <div className="check-domain-error-message">
              {result.error.message}
            </div>
          </div>
        </div>
      )}
      {!loading && result && !result.error && (
        <div className="check-domain-result">
          <table className="monitoring-table">
            <thead>
              <MonitoringTableHead
                columns={DEFAULT_COLUMNS}
                showTooltip={showTooltip}
                moveTooltip={moveTooltip}
                hideTooltip={hideTooltip}
                HeaderHelpIcon={HeaderHelpIcon}
              />
            </thead>
            <tbody>
              <tr>
                <td>{result.domain_name}</td>
                <td>Категория</td>
                <td>
                  <span
                    className={`status-pill status-${getNsdiStatus(result.nsdi).variant} status-has-tooltip`}
                    onMouseEnter={(e) =>
                      showTooltip(
                        e,
                        buildSimpleTooltip(
                          "НСДИ",
                          getNsdiStatus(result.nsdi).label,
                        ),
                      )
                    }
                    onMouseMove={moveTooltip}
                    onMouseLeave={hideTooltip}
                  >
                    {getNsdiStatus(result.nsdi).label}
                  </span>
                </td>
                <td>
                  <span
                    className={`status-pill status-${getDnsStatus(result.russian_dns).variant} status-has-tooltip`}
                    onMouseEnter={(e) => {
                      setScrollHover(true);
                      showTooltip(e, buildDnsTooltip("RU", result.russian_dns));
                    }}
                    onMouseMove={moveTooltip}
                    onMouseLeave={hideTooltip}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      setScrollHover(true);
                      showTooltip(e, buildDnsTooltip("RU", result.russian_dns));
                    }}
                  >
                    {getDnsStatus(result.russian_dns).label}
                  </span>
                </td>
                <td>
                  <span
                    className={`status-pill status-${getDnsStatus(result.global_dns).variant} status-has-tooltip`}
                    onMouseEnter={(e) => {
                      setScrollHover(true);
                      showTooltip(
                        e,
                        buildDnsTooltip("Global", result.global_dns),
                      );
                    }}
                    onMouseMove={moveTooltip}
                    onMouseLeave={hideTooltip}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      setScrollHover(true);
                      showTooltip(
                        e,
                        buildDnsTooltip("Global", result.global_dns),
                      );
                    }}
                  >
                    {getDnsStatus(result.global_dns).label}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <HoverTooltip
        data={tooltip}
        scrollRef={tooltipScrollRef}
        ref={tooltipRootRef}
      />
    </div>
  );
};

export default CheckDomain;
